import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import UnifiedMenu from '../components/UnifiedMenu';
import { ArrowLeft, Mail, MailOpen, CheckCheck, Loader } from 'lucide-react';
import { toast } from 'sonner';

export default function Inbox({ user, onLogout }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMsg, setSelectedMsg] = useState(null);

  useEffect(() => { fetchInbox(); }, []);

  const fetchInbox = async () => {
    try {
      const res = await axios.get(`${API}/inbox`);
      setMessages(res.data);
    } catch (err) {
      console.error('Failed to fetch inbox', err);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    await axios.post(`${API}/inbox/mark-read`, { id });
    setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
  };

  const markAllRead = async () => {
    await axios.post(`${API}/inbox/mark-read`, { id: 'all' });
    setMessages(prev => prev.map(m => ({ ...m, read: true })));
    toast.success('All messages marked as read');
  };

  const openMessage = (msg) => {
    setSelectedMsg(msg);
    if (!msg.read) markRead(msg.id);
  };

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 overflow-hidden">
      <UnifiedMenu active="notifications" stats={null} currentQuote="" recentMoods={[]} onLogout={onLogout} />

      {/* Header */}
      <div className="flex-none px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-full hover:bg-stone-100">
              <ArrowLeft className="w-5 h-5 text-warm-charcoal" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
                Inbox
              </h1>
              {unreadCount > 0 && (
                <p className="text-xs text-hunter-green font-medium">{unreadCount} unread</p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-hunter-green bg-hunter-green/10 hover:bg-hunter-green/20 transition-colors"
              style={{ touchAction: 'manipulation' }}
              data-testid="mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {loading ? (
          <div className="flex justify-center py-12"><Loader className="w-6 h-6 animate-spin text-hunter-green" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16">
            <Mail className="w-10 h-10 text-warm-charcoal/20 mx-auto mb-3" />
            <p className="text-sm text-warm-charcoal/40">No messages yet</p>
            <p className="text-xs text-warm-charcoal/30 mt-1">Notifications will appear here</p>
          </div>
        ) : (
          <div className="mt-1 divide-y divide-stone-100">
            {messages.map(msg => (
              <button
                key={msg.id}
                onClick={() => openMessage(msg)}
                className={`w-full text-left px-3 py-2 block overflow-hidden ${
                  msg.read ? 'bg-white' : 'bg-hunter-green/5'
                }`}
                style={{ touchAction: 'manipulation', maxHeight: '56px' }}
                data-testid={`inbox-msg-${msg.id}`}
              >
                <div className="flex items-center gap-2" style={{ height: '40px' }}>
                  <div className="flex-shrink-0">
                    {msg.read ? (
                      <MailOpen className="w-3.5 h-3.5 text-warm-charcoal/25" />
                    ) : (
                      <div className="relative">
                        <Mail className="w-3.5 h-3.5 text-hunter-green" />
                        <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-hunter-green rounded-full" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={`text-xs truncate ${msg.read ? 'text-warm-charcoal/60' : 'font-semibold text-warm-charcoal'}`}>
                        {msg.subject}
                      </h3>
                      <span className="text-[9px] text-warm-charcoal/35 flex-shrink-0">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className={`text-[11px] truncate ${msg.read ? 'text-warm-charcoal/35' : 'text-warm-charcoal/50'}`} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {(msg.message || '').replace(/[\n\r]/g, ' ').replace(/\s+/g, ' ').trim()}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Message Detail Modal — matches email stationery */}
      {selectedMsg && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedMsg(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Hero with backdrop */}
            <div
              className="relative"
              style={{
                backgroundImage: `url(https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&q=80)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="bg-gradient-to-b from-[#1a2e1a]/70 to-[#3A5A40]/85 px-5 pt-5 pb-6 text-center">
                <p className="text-[10px] text-white/60 uppercase tracking-[3px] mb-1">MindMyMojo</p>
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {selectedMsg.subject}
                </h2>
                <p className="text-[10px] text-white/50 mt-2">
                  {new Date(selectedMsg.created_at).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setSelectedMsg(null)}
                className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/30 backdrop-blur-sm text-white flex items-center justify-center text-sm hover:bg-black/50"
              >
                x
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)' }}>
              <p className="text-sm text-warm-charcoal mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                Hi {user?.name || 'there'},
              </p>
              <div className="text-sm text-warm-charcoal/80 leading-relaxed whitespace-pre-line mt-3">
                {selectedMsg.message}
              </div>

              {/* Sign-off */}
              <div className="mt-6 pt-4 border-t border-stone-200">
                <p className="text-xs text-warm-charcoal/50">
                  With warmth,<br />
                  <strong className="text-hunter-green">The MindMyMojo Team</strong>
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-stone-50 border-t border-stone-200 text-center rounded-b-2xl">
              <p className="text-[10px] text-warm-charcoal/30">mindmymojo.com</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
